import { UserCompanyRole } from '../enums/userCompanyRole.enum'
import { ICompanyMemberPure, ICompanyPure } from '../models/Company'
import {
  IOnboardingInstancePure,
  IOnboardingInstancePopulated,
} from '../models/OnboardingInstance'

// Check if user is lead of a team
export const isLeadOfTeam = (user: ICompanyMemberPure, teamId?: string) => {
  return (
    user.teams.find((team) => '' + team.teamId === '' + teamId)?.role ===
    UserCompanyRole.LEAD
  )
}

// Checks if a user is an admin
export const isAdmin = (member: ICompanyMemberPure) => member.isAdmin

// Check if a user is support user
export const isSupport = (email: string) =>
  email ===
  (process.env.EMAIL_SUPPORT_USER || process.env.REACT_APP_EMAIL_SUPPORT_USER)

// Member is considered to be a strict lead of someone IF they are the custom lead of the person, OR they are the team lead of the other member
// and the other member does not have a custom lead
// In general, team leads lose some privileges when there is a custom lead. If that is the case, this function needs to be used
export const isMemberStrictLeadOfMember = (
  lead: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + lead._id === '' + member._id) {
    return false
  }

  // If the member has a custom lead, only the custom lead is the lead of the member
  if (member.customLeadId) {
    return isMemberCustomLeadOfMember(lead, member)
  }

  return isMemberGenerallyLeadOfMember(lead, member)
}

// Simply checks if someone is the custom lead of someone else
export const isMemberCustomLeadOfMember = (
  lead: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + lead._id === '' + member._id) {
    return false
  }

  return !!member.customLeadId && member.customLeadId + '' === '' + lead._id
}

// Member is considered team lead of someone if the member has a role of lead and both of them are in the same team
export const isMemberTeamLeadOfMember = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  if ('' + leadCandidate._id === '' + member._id) {
    return false
  }

  // Check if the lead is leading teams in which the member is also participating
  // Team leads within the same team are not considered a team lead of each other
  const memberTeamIds =
    member.teams
      .filter((t) => t.role === UserCompanyRole.MEMBER)
      .map((team) => '' + team.teamId) || []
  const leadTeamIds =
    leadCandidate.teams
      .filter((t) => t.role === UserCompanyRole.LEAD)
      .map((team) => '' + team.teamId) || []

  // If any of the teams are equal, the lead is indeed a lead of this member
  if (leadTeamIds.some((item) => memberTeamIds.includes(item))) {
    return true
  }

  return false
}

export const teamHasTeamLead = (teamId: string, company: ICompanyPure) =>
  company.members
    .filter((member) => !member.isDisabled)
    .some((member) =>
      member.teams.some(
        (mTeam) =>
          String(mTeam.teamId) === String(teamId) &&
          mTeam.role === UserCompanyRole.LEAD
      )
    )

// Check if member is a buddy of another member
export const isMemberBuddyOfAnotherMember = (
  buddy: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  return (
    member.buddies &&
    member.buddies.some((buddyId) => '' + buddyId === '' + buddy._id)
  )
}

// General condition for someone being a lead of someone else
// See comments in code for the exact checks performed
export const isMemberGenerallyLeadOfMember = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure
) => {
  // Someone cannot ever be a lead of himself
  if ('' + leadCandidate._id === '' + member._id) {
    return false
  }

  // If the lead candidate is the team lead of the member, then he / she is a lead
  if (isMemberTeamLeadOfMember(leadCandidate, member)) {
    return true
  }

  // If the member has a custom lead, check if the passed lead is that one
  if (isMemberCustomLeadOfMember(leadCandidate, member)) {
    return true
  }

  return false
}

export const getLeadsOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure[] => {
  return company.members.filter(
    (candidateLead) =>
      !candidateLead.isDisabled &&
      isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}

export const doesMemberHaveTeamLead = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): boolean => {
  const activeMembers = company.members.filter((m) => !m.isDisabled)

  return activeMembers.some((candidateLead) =>
    isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}
export const getTeamLeadOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure | undefined => {
  const activeMembers = company.members.filter((m) => !m.isDisabled)

  return activeMembers.find((candidateLead) =>
    isMemberGenerallyLeadOfMember(candidateLead, member)
  )
}

export const isMemberOnboardingGuide = (
  leadCandidate: ICompanyMemberPure,
  member: ICompanyMemberPure,
  onboardingInstance: IOnboardingInstancePure | IOnboardingInstancePopulated
) => {
  return (
    onboardingInstance.memberId + '' === member._id + '' &&
    leadCandidate._id + '' === onboardingInstance.onboardingBuddyId + ''
  )
}
export const getCustomLeadOfMember = (
  member: ICompanyMemberPure,
  company: ICompanyPure
): ICompanyMemberPure | undefined => {
  const activeMembers = company.members.filter((m) => !m.isDisabled)

  return activeMembers.find((candidateLead) =>
    isMemberCustomLeadOfMember(candidateLead, member)
  )
}

export const getMemberRole = (
  member?: ICompanyMemberPure
): UserCompanyRole | undefined => {
  if (!member) {
    return undefined
  }
  return member.teams.some((t) => t.role === UserCompanyRole.LEAD)
    ? UserCompanyRole.LEAD
    : UserCompanyRole.MEMBER
}
